/**
 * Calculates the percentage difference between two numbers.
 *
 * @param current - The current value
 * @param previous - The previous value to compare against
 * @returns The percentage difference, or null if the previous value is zero
 *
 * @example
 * const diff = calculatePercentageDifference(150, 100);
 * // diff will be 50 (representing a 50% increase)
 */
export const calculatePercentageDifference = (
  current: number,
  previous: number
): number | null => {
  if (previous === 0 || previous == null) {
    return null; // Return null to indicate an undefined or infinite percentage difference
  }

  const difference = ((current - previous) / previous) * 100;
  return Math.abs(Math.round(difference));
};

/**
 * Determines the trend (up or down) between two numbers.
 *
 * @param current - The current value
 * @param previous - The previous value to compare against
 * @returns 'up' if current > previous, 'down' if current < previous, null if equal
 *
 * @example
 * const trend = getTrend(150, 100);
 * // trend will be 'up'
 */
export const getTrend = (
  current: number,
  previous: number
): 'up' | 'down' | null => {
  if (current > previous) return 'up';
  if (current < previous) return 'down';
  return null;
};
